import React, { useEffect, useState } from "react";
import { Button, Typography, Select, message, Flex, Splitter, Space, Segmented, notification, } from "antd";
import Editor from "@monaco-editor/react";
import { useDispatch } from "react-redux";
import { postData, putData } from "../../../actions/developer";
import { useParams } from "react-router-dom";
import { decryptData, encryptData } from "../../../actions/crypto";
import { getData } from "../../../actions/apiCall";
import CountdownTimer from "../ExamRoom/CountdownTimer";
import Icon, { DoubleLeftOutlined, DoubleRightOutlined, LoadingOutlined, PlayCircleTwoTone } from '@ant-design/icons';
import { languageMap } from "../../hrm/TestSetup/CodingQuestions";
import moment from "moment";
import { examConfig } from "../../../config/examConfig";
import { startTestMonitoring, stopTestMonitoring } from "../../../utils/testMonitoring";

const { Title } = Typography;

const colors = {
    pending: "#FF9500",
    passed: "#009921",
    failed: "#FF0000",
    error: "#9F0000",
}

const CodeEditorComponent: React.FC<any> = () => {
    const [language, setLanguage] = useState<string>("63");
    const [code, setCode] = useState<string>("");
    const [size, setSize] = useState(10);
    const [leftSize, setLeftSize] = useState(30);
    const dispatch = useDispatch();
    const { id } = useParams();

    const [questionData, setQuestionData] = useState<any>([]);
    const [activeSeat, setActiveSeat] = useState<string>("");
    const [currentQuestion, setCurrentQuestion] = useState<any>(null);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(0);
    const [timeRequired, setTimeRequired] = useState<number>(0);
    const [outputWindow, setOutPutWindow] = useState<string>("Cases");
    const [loadingOutput, setLoadingOutput] = useState<boolean>(false);
    const [codeOutput, setCodeOutput] = useState([]);
    const [testDetails, setTestDetails] = useState<any>(null);
    const [attempts, setAttempts] = useState<{ current: number; max: number; remaining: number }>({ current: 0, max: 3, remaining: 3 });

    useEffect(() => {
        if (id) {
            getTestDetails()
        }

        // Start monitoring when component mounts
        if (id) {
            startTestMonitoring(id);
        }

        // Cleanup: stop monitoring when component unmounts
        return () => {
            stopTestMonitoring();
        };
    }, [id]);

    const getTotalTime = () => {
        const coddingQuestions: any = testDetails?.coddingQuestions;
        if (coddingQuestions.length > 0) {
            return coddingQuestions.reduce((acc: number, ele: any) => acc + ele.timeRequired, 0);
        }
        return 0;
    }

    const getTestDetails = async () => {
        try {
            const resData = await dispatch(getData(`/accel-test/${id}`));
            if (resData.code === 200) {
                const data = decryptData(resData.data);

                if (data?.testStatus === 'completed') {
                    notification.error({ message: 'Test Already Completed' });
                    location.href = `/instructions/${id}`; // Redirect to Error page
                    return;
                }

                if (data?.testStatus === 'pending') {
                    notification.error({ message: 'Test Not Started' });
                    location.href = `/instructions/${id}`; // Redirect to Error page
                    return;
                }

                if (data?.testStatus === 'expired') {
                    notification.error({ message: 'Test Expired' });
                    location.href = `/instructions/${id}`; // Redirect to Error page
                    return;
                }

                if (data?.testStatus === 'cancelled') {
                    notification.error({ message: 'Test Cancelled' });
                    location.href = `/instructions/${id}`; // Redirect to Error page
                    return;
                }

                setTestDetails(data);
                if (data?.coddingQuestions?.length == 0) {
                    notification.error({ message: 'No Test Found' });
                    location.href = `/instructions/${id}`; // Redirect to Error page
                    return;
                }
            } else {
                location.href = `/instructions/${id}`; // Redirect to Error page
                return;
            }
        } catch (error) {
            console.log(error);
            location.href = `/instructions/${id}`; // Redirect to Error page
            return;
        }
    }

    const checkMcqTimeCompleted = () => {
        const coddingStartedAt = testDetails?.coddingStartedAt;

        if (!coddingStartedAt) {
            location.href = `/instructions/${id}`;
        };

        const totalTime = getTotalTime(); // in minutes
        const endTime = moment(coddingStartedAt).add(totalTime, 'minutes');
        const now = moment();

        if (now.isAfter(endTime)) {
            handleSubmit();
        }
    };

    useEffect(() => {
        if (testDetails && questionData.length > 0) {
            checkMcqTimeCompleted();
        }
    }, [questionData]);

    const handleSubmit = async () => {
        const codingQuestions = []
        questionData.forEach((ele: any) => {
            const code = JSON.parse(localStorage.getItem(ele._id));
            codingQuestions.push({
                questionId: ele._id,
                submitedCode: code
            });
        });
        const resData = await dispatch(putData(`/accel-test/bulk/coding/${id}`, { data: encryptData({ codingQuestions }) }));
        if (resData) {
            location.href = `/instructions/${id}`;
        } else {
            message.error("Error submitting code.");
        }
    };

    useEffect(() => {
        if (testDetails) {
            const coddingQuestions: any = testDetails?.coddingQuestions;
            if (coddingQuestions.length > 0) {
                const totalTime = coddingQuestions.reduce((acc: number, ele: any) => acc + ele.timeRequired, 0);
                setTimeRequired(totalTime);
                loadSection(coddingQuestions[currentSectionIndex]?.sectionId);
            }
        }
    }, [testDetails]);

    useEffect(() => {
        try {

            if (activeSeat) {
                const data = questionData.find((ele: any) => ele._id === activeSeat);
                if (data) {
                    setCurrentQuestion(data);
                    setLanguage(languageMap[data?.codingQuestion?.language.toString()] || "javascript");
                    const code = JSON.parse(localStorage.getItem(data._id));
                    if (code) {
                        setCode(JSON.parse(localStorage.getItem(data._id)) || "");
                    } else {
                        setCode(data?.codingQuestion?.code || "console.log('Hello, World!');");
                    }
                    const code_status = localStorage.getItem('code_status');
                    const decryptedCodeStatus = code_status ? decryptData(code_status) : {};
                    const status = decryptedCodeStatus[data._id] || [];
                    setCodeOutput(status);

                    // Set attempts from question data
                    const currentAttempts = data?.runAttempts || 0;
                    const maxAttempts = data?.maxRunAttempts || 3;
                    setAttempts({
                        current: currentAttempts,
                        max: maxAttempts,
                        remaining: Math.max(0, maxAttempts - currentAttempts)
                    });
                }
            }
        } catch (error) {
            console.error("Error loading section:", error);
        }
    }, [activeSeat, questionData]);


    const loadSection = async (sectionId: string) => {
        if (!id || !sectionId) return;
        const resData = await dispatch(getData(`/accel-test/${id}/${encryptData(sectionId)}/coding`));
        if (resData.code === 200) {
            const data = decryptData(resData.data);
            setQuestionData(data);
            setActiveSeat(data[0]?._id || "");
            setCurrentIndex(0);
        }
    };

    const handleNext = () => {
        if (currentIndex < questionData.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setActiveSeat(questionData[currentIndex + 1]._id);
        } else {
            const coddingQuestions: any = testDetails?.coddingQuestions;
            if (currentSectionIndex < coddingQuestions.length - 1) {
                setCurrentSectionIndex(currentSectionIndex + 1);
                loadSection(coddingQuestions[currentSectionIndex + 1].sectionId);
            }
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
            setActiveSeat(questionData[currentIndex - 1]._id);
        } else {
            if (currentSectionIndex > 0) {
                setCurrentSectionIndex(currentSectionIndex - 1);
                loadSection(testDetails.coddingQuestions[currentSectionIndex - 1].sectionId);
            }
        }
    };

    const isLastQuestion = () => {
        const coddingQuestions: any = testDetails?.coddingQuestions || [];
        return currentSectionIndex === coddingQuestions.length - 1 && currentIndex === questionData.length - 1;
    };

    const handleCodeChange = (newCode: string | undefined) => {
        if (newCode) {
            setCode(newCode);
            localStorage.setItem(currentQuestion._id, JSON.stringify(newCode));
        };
    };

    const handleRunCode = async () => {
        // Check if attempts exceeded
        if (attempts.remaining <= 0) {
            message.warning(`Maximum attempts (${attempts.max}) reached for this question. You cannot run code anymore.`);
            return;
        }

        try {
            setLoadingOutput(true);
            setCodeOutput([]);
            setSize(30);
            const submissions = currentQuestion?.codingQuestion?.cases.map(tc => ({
                language_id: currentQuestion?.codingQuestion.language,
                source_code: code,
                stdin: tc.input,
                expected_output: tc.output
            }));

            const response = await dispatch(postData('/code-run', { data: encryptData({ submissions, questionId: currentQuestion._id }) }));

            if (response.code == 200) {
                // Handle new response format with attempts
                const results = response.data.results || response.data;
                const attemptInfo = response.data.attempts || attempts;

                setCodeOutput(results);

                // Update attempts state
                setAttempts({
                    current: attemptInfo.current,
                    max: attemptInfo.max,
                    remaining: attemptInfo.remaining
                });

                // Update question data with new attempts
                setQuestionData((prev: any[]) =>
                    prev.map((q: any) =>
                        q._id === currentQuestion._id
                            ? { ...q, runAttempts: attemptInfo.current, maxRunAttempts: attemptInfo.max }
                            : q
                    )
                );

                const code_status = localStorage.getItem('code_status');
                const decryptedCodeStatus = code_status ? decryptData(code_status) : {};
                decryptedCodeStatus[currentQuestion._id] = results;
                localStorage.setItem('code_status', encryptData(decryptedCodeStatus));

                if (results[0]?.compile_output || results[0]?.stderr) {
                    setOutPutWindow("Console");
                } else {
                    setOutPutWindow("Cases");
                }

                // Show warning if attempts are running low
                if (attemptInfo.remaining === 1) {
                    message.warning(`Last attempt remaining! You have 1 more attempt for this question.`);
                } else if (attemptInfo.remaining === 0) {
                    message.error(`Maximum attempts (${attemptInfo.max}) reached. You cannot run code anymore.`);
                }
            } else {
                // Handle error response
                if (response.message && response.message.includes('Maximum attempts')) {
                    message.error(response.message);
                    setAttempts(prev => ({ ...prev, remaining: 0 }));
                } else {
                    message.error("Error executing code.");
                }
            }
        } catch (error) {
            message.error("Error executing code.");
        } finally {
            setLoadingOutput(false);
        }
    };


    const getColor = (status: any) => {
        switch (true) {
            case status == 3:
                return {
                    color: colors.passed,
                    text: "Success",
                };
            case status == 4:
                return {
                    color: colors.failed,
                    text: "Failed",
                }
            case status > 4:
                return {
                    color: colors.error,
                    text: "Error",
                };
            default:
                return {
                    color: colors.pending,
                    text: "Pending",
                };
        }
    }

    return (
        <div className="bg-white flex flex-col overflow-hidden h-screen">
            <div className="flex justify-between items-center border-b border-gray-200 p-2">
                <img src={require('./../../../assets/logo/logo.png')} alt="logo" className="w-40" />
                {examConfig.enableTimer && (
                    <CountdownTimer
                        durationMinutes={timeRequired}
                        startTime={testDetails?.coddingStartedAt}
                        timeUp={() => handleSubmit()}
                    />
                )}
                <Space>
                    {currentIndex > 0 || currentSectionIndex > 0 ? (
                        <Button type="default" icon={<DoubleLeftOutlined />} onClick={handlePrev}>
                            Previous
                        </Button>
                    ) : null}

                    {isLastQuestion() ? (
                        <Button type="primary" className="items-center" onClick={() => handleSubmit()}>
                            Submit
                        </Button>
                    ) : (
                        <Button type="primary" icon={<DoubleRightOutlined />} onClick={handleNext}>
                            Next
                        </Button>
                    )}
                </Space>
            </div>
            <div className="flex flex-grow justify-center items-stretch h-fit overflow-hidden">
                <div className="flex justify-between w-[40px] border-r border-gray-200">
                    <div className="mx-auto">
                        {questionData.map((ele, index) => (
                            <div
                                key={ele._id}
                                onClick={() => setActiveSeat(ele._id)}
                                className={` mt-2 w-7 h-7 flex items-center text-black justify-center rounded-full text-lg font-semibold cursor-pointer hover:border-2 hover:border-red-500
                                    ${ele._id === activeSeat ? "border-2 border-red-500 text-red-500" : "border border-gray-400 text-gray-700"}
                                `}
                            >
                                {index + 1}
                            </div>
                        ))}
                    </div>
                </div>
                <Splitter className="w-full">
                    <Splitter.Panel collapsible min="20%" size={`${leftSize}%`}>
                        <div className="border-b border-gray-200 p-2">
                            <Flex justify="space-between">
                                <Title level={4} className="">
                                    {currentQuestion?.codingQuestion?.question}
                                </Title>
                                {leftSize == 30 ? <Icon component={DoubleLeftOutlined} onClick={() => setLeftSize(20)} /> :
                                    <Icon component={DoubleRightOutlined} onClick={() => setLeftSize(30)} />}
                            </Flex>
                        </div>
                        <div className="ql-container ql-snow !border-none">
                            <div
                                key={currentQuestion?.codingQuestion?.details}
                                className="ql-editor p-4"
                                dangerouslySetInnerHTML={{
                                    __html: currentQuestion?.codingQuestion?.details || "No question found."
                                }}>
                            </div>
                        </div>
                    </Splitter.Panel>
                    <Splitter.Panel min={'40%'} collapsible className="border-l border-gray-200 h-full bg-[#1e1e1e]">
                        <Splitter layout="vertical" className="h-full">
                            <Splitter.Panel min={'40%'} collapsible className="h-full">
                                <div className="overflow-hidden h-full">
                                    <Editor
                                        className="border-none p-4 h-full"
                                        language={languageMap[language ?? 63] || "javascript"}
                                        theme="vs-dark"
                                        value={code}
                                        onChange={handleCodeChange}
                                    />
                                </div>
                            </Splitter.Panel>
                            <Splitter.Panel collapsible min="10%" size={`${size}%`} className="h-full bg-white">
                                <div className="flex justify-between mt-2 border-b border-gray-200 p-2">
                                    <Space direction="horizontal">
                                        {size == 10 ? <Icon component={DoubleRightOutlined} rotate={270} onClick={() => setSize(30)} /> :
                                            <Icon component={DoubleRightOutlined} rotate={90} onClick={() => setSize(10)} />}
                                        <Segmented
                                            options={['Cases', 'Console']}
                                            value={outputWindow}
                                            onChange={(value) => {
                                                setOutPutWindow(value);
                                            }}
                                        />
                                    </Space>

                                    <Space>
                                        <div className="text-sm text-gray-600">
                                            <span className={attempts.remaining === 0 ? "text-red-500 font-semibold" : attempts.remaining === 1 ? "text-orange-500 font-semibold" : ""}>
                                                Attempts: {attempts.current}/{attempts.max} ({attempts.remaining} remaining)
                                            </span>
                                        </div>
                                        <Button
                                            type="primary"
                                            loading={loadingOutput}
                                            disabled={loadingOutput || attempts.remaining <= 0}
                                            icon={<PlayCircleTwoTone className="text-lg mt-[5px]" />}
                                            onClick={handleRunCode}
                                            title={attempts.remaining <= 0 ? `Maximum attempts (${attempts.max}) reached` : `Run code (${attempts.remaining} attempt${attempts.remaining !== 1 ? 's' : ''} remaining)`}>
                                            Run Code
                                        </Button>
                                    </Space>
                                </div>
                                <div className="pt-4 p-3 h-full overflow-auto bg-[#e6e9f3]">
                                    {outputWindow == 'Cases' && <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-3">
                                        {currentQuestion?.codingQuestion?.cases.map((ele, index) => <div className={`bg-white flex flex-rows justify-between items-center p-2 border border-[${getColor(codeOutput[index]?.status_id)?.color}] rounded-md px-4`} key={index}>
                                            <div className="text-gray-800 font-semibold">
                                                Case {index + 1}
                                            </div>
                                            <div className="">
                                                <div className={`bg-[${getColor(codeOutput[index]?.status_id)?.color}] w-full h-8 justify-center flex items-center rounded-lg text-white bold px-8`}>
                                                    {loadingOutput && <LoadingOutlined spin={loadingOutput} className="text-white" />}
                                                    {!loadingOutput && getColor(codeOutput[index]?.status_id)?.text}
                                                </div>
                                            </div>
                                        </div>)}
                                    </div>}
                                    {outputWindow == 'Console' && <pre className="text-gray-800 whitespace-pre-wrap">
                                        {loadingOutput ? <LoadingOutlined spin={loadingOutput} className="text-primary text-2xl" /> :
                                            codeOutput[0]?.compile_output || codeOutput[0]?.stderr || codeOutput[0]?.stdout || "No Error"}</pre>}
                                </div>
                            </Splitter.Panel>
                        </Splitter>
                    </Splitter.Panel>
                </Splitter>
            </div>
        </div >
    );
};

export default CodeEditorComponent;

